import { useEffect, useEffectEvent } from 'react';

import dayjs from 'dayjs';

import { CAST_AI } from '@cast/constants';
import {
  DialogContent,
  Spinner,
  DialogFooter,
  Typography,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { Cluster } from '@cast/types';
import { useScheduledJob } from '@cast/utils';

import {
  BackButton,
  ClusterDialogTitle,
  DialogHeading,
  StepContainer,
} from 'hooks/dialogs/cluster/_components/new-onboarding';

import { useGetNewlyOnboardedClusters } from '../../_hooks/useGetNewlyOnboardedClusters';

const CONNECTION_TIMEOUT_MS = 5 * 60 * 1000;

const CONTENT_LEFT_PADDING = 46;
const DIALOG_CONTENT_PADDING = 24;

const useMonitorNewlyOnboardedCluster = ({
  clusterScanTimeFrom,
  onClusterConnected,
}: {
  clusterScanTimeFrom: Date;
  onClusterConnected: (cluster: Cluster) => void;
}) => {
  const onClusterConnectedEvent = useEffectEvent(onClusterConnected);
  const { newConnections } = useGetNewlyOnboardedClusters({
    provider: 'all',
    polling: true,
    clusterScanTimeFrom,
  });
  const newlyOnboardedCluster = newConnections[0];

  useEffect(() => {
    if (newlyOnboardedCluster) {
      onClusterConnectedEvent(newlyOnboardedCluster);
    }
  }, [newlyOnboardedCluster]);
};

const useConnectionTimeout = ({ onTimeout }: { onTimeout: () => void }) => {
  const onTimeoutEvent = useEffectEvent(onTimeout);
  const { start, clear } = useScheduledJob();
  useEffect(() => {
    start({
      when: dayjs().add(CONNECTION_TIMEOUT_MS, 'milliseconds'),
      job: () => onTimeoutEvent(),
    });

    return clear;
  }, [clear, start]);
};

type Props = {
  clusterScanTimeFrom: Date;
  onBack: () => void;
  onClose: () => void;
  onClusterConnected: (cluster: Cluster) => void;
  onTimeout: () => void;
};

export const ConnectingStep = ({
  clusterScanTimeFrom,
  onBack,
  onClose,
  onClusterConnected,
  onTimeout,
}: Props) => {
  useMonitorNewlyOnboardedCluster({ clusterScanTimeFrom, onClusterConnected });
  useConnectionTimeout({ onTimeout });

  return (
    <StepContainer position="relative" data-testid="castctl-connecting-step">
      <DialogHeading onClose={onClose} hideTerraformLink>
        <Stack direction="row" gap={12} alignItems="center">
          <Spinner size={32} color="secondary" />
          <ClusterDialogTitle>Connecting your cluster</ClusterDialogTitle>
        </Stack>
      </DialogHeading>
      <DialogContent sx={{ p: DIALOG_CONTENT_PADDING, pb: 0 }}>
        <Stack gap={8}>
          <Stack pl={CONTENT_LEFT_PADDING} gap={2}>
            <Typography variant="P14M">What happens next?</Typography>
            <Stack pl={8}>
              <Typography variant="P12R" color="textSecondary">
                • {CAST_AI} components will be deployed to your cluster
              </Typography>
              <Typography variant="P12R" color="textSecondary">
                • Data collection will begin within 5 minutes
              </Typography>
              <Typography variant="P12R" color="textSecondary">
                • Product recommendations will appear in 15-30 minutes
              </Typography>
            </Stack>
          </Stack>

          <Stack
            py={8}
            px={DIALOG_CONTENT_PADDING}
            pl={DIALOG_CONTENT_PADDING + CONTENT_LEFT_PADDING}
            mx={-DIALOG_CONTENT_PADDING}
            bgcolor={colorOrToken('blue.50', tokens.theme.bg.status.infoSubtle)}
            borderTop={colorOrToken(
              'blue.100',
              tokens.theme.border.status.infoSubtle
            )}
            borderBottom={colorOrToken(
              'blue.100',
              tokens.theme.border.status.infoSubtle
            )}
          >
            <Typography variant="P12R" color="textSecondary">
              Closing this dialog will not affect the process.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogFooter
        sx={{ borderTop: 'none' }}
        start={<BackButton onClick={onBack} />}
      />
    </StepContainer>
  );
};
